import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Modal, Select, Slider, Switch, message } from 'antd'
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons'
import { listEngines, runTemplateCutout, type AutoProcessTemplate } from '../../api/client'
import type { EngineInfo } from '../../types'
import { bustUrl } from '../StageFlow'
import EditorHeader, { confirmDiscardAndClose } from './EditorHeader'
import { MODAL_SURFACE_CLASS, useModalScrollLock } from '../../saas/utils/scrollLock'

/**
 * Auto-process template's 原图→透明底图 step — deliberately NOT the full
 * manual DualPaneCutoutEditor (画笔/点选/矩形/套索). A template only stores
 * batch-reusable *automatic* parameters (spec 五: "这里不需要...保留画笔/去除
 * 画笔/正向点击/负向点击/手动修补蒙版/手动画选区...因为自动处理模板的目标是
 * 保存可批量复用的自动化参数，不应该保存只对某一张图片有效的人工选区或手绘
 * 蒙版"). Every field change re-runs real auto-cutout against the template's
 * test image (debounced) so the preview is always the actual result.
 */

interface CutoutFields {
  engine: string
  edge_smooth: number
  feather: number
  dilate: number
  erode: number
  decontaminate: boolean
  decontaminate_strength: number
}

function fieldsFromTemplate(t: AutoProcessTemplate): CutoutFields {
  const c = t.params.cutout
  const p = (c.parameters || {}) as Record<string, unknown>
  return {
    engine: c.engine || 'rembg',
    edge_smooth: Number(p.edge_smooth ?? 0.5),
    feather: Number(p.feather ?? 0),
    dilate: Number(p.dilate ?? 0),
    erode: Number(p.erode ?? 0),
    decontaminate: p.decontaminate !== false,
    decontaminate_strength: Number(p.decontaminate_strength ?? 0.6),
  }
}

function fieldsEqual(a: CutoutFields, b: CutoutFields): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** This editor's system-defined defaults (恢复默认) — deliberately the same
 * fallback values `fieldsFromTemplate` already uses for missing/legacy
 * fields, so "恢复默认" always means the same thing as "an untouched
 * template". Only affects in-editor state; still requires a "保存" for the
 * live-preview architecture below to be treated as final (spec 一.5). */
const SYSTEM_DEFAULT_CUTOUT_FIELDS: CutoutFields = {
  engine: 'rembg',
  edge_smooth: 0.5,
  feather: 0,
  dilate: 0,
  erode: 0,
  decontaminate: true,
  decontaminate_strength: 0.6,
}

interface Props {
  template: AutoProcessTemplate
  open: boolean
  onClose: () => void
  onSaved: (t: AutoProcessTemplate) => void
}

export default function TemplateCutoutEditorModal({ template, open, onClose, onSaved }: Props) {
  const { t } = useTranslation('autoProcess')
  useModalScrollLock(open)
  const [engines, setEngines] = useState<EngineInfo[]>([])
  const [fields, setFields] = useState<CutoutFields>(() => fieldsFromTemplate(template))
  const [current, setCurrent] = useState<AutoProcessTemplate>(template)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState('')
  const savedInitialRef = useRef<CutoutFields>(fieldsFromTemplate(template))
  const debounceRef = useRef<number | null>(null)

  useEffect(() => {
    if (!open) return
    const initial = fieldsFromTemplate(template)
    savedInitialRef.current = initial
    setFields(initial)
    setCurrent(template)
    setError('')
    listEngines()
      .then((r) => setEngines(r.data))
      .catch(() => undefined)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template.id])

  const apply = async (f: CutoutFields, cascade = false) => {
    setApplying(true)
    setError('')
    try {
      const { data } = await runTemplateCutout(current.id, {
        engine: f.engine,
        parameters: {
          edge_smooth: f.edge_smooth,
          feather: f.feather,
          dilate: f.dilate,
          erode: f.erode,
          decontaminate: f.decontaminate,
          decontaminate_strength: f.decontaminate_strength,
        },
        cascade,
      })
      setCurrent(data)
      if (data.cutout_status === 'failed') {
        setError(data.cutout_error || t('templateCutoutEditor.autoCutoutFailed'))
      }
      return data
    } catch (e) {
      setError((e as Error).message || t('templateCutoutEditor.autoCutoutFailed'))
      throw e
    } finally {
      setApplying(false)
    }
  }

  const scheduleApply = (next: CutoutFields) => {
    setFields(next)
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => {
      apply(next).catch(() => undefined)
    }, 650)
  }

  const flushApply = async (cascade = false) => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    return apply(fields, cascade)
  }

  const isDirty = !fieldsEqual(fields, savedInitialRef.current) || current.template_version !== template.template_version

  const handleApplyNow = async () => {
    try {
      await flushApply()
      message.success(t('templateCutoutEditor.previewRegenerated'))
    } catch {
      /* error already shown inline */
    }
  }

  /** 保存 (spec 一.2/六): the top-right primary action. This editor's preview
   * is architecturally already live (every field change re-runs real
   * auto-cutout against the template's test image, debounced) — unlike the
   * background/shadow editors there's no separate "commit" step on the
   * backend, so "保存" here just flushes any pending debounced change and
   * hands the final committed result back to the parent, which is what
   * triggers the downstream cascade rerun (色底 → 阴影, spec 七). */
  const doSave = async () => {
    try {
      const data = await flushApply(true)
      onSaved(data || current)
      onClose()
    } catch {
      /* keep modal open so the user can fix the failing params */
    }
  }

  /** 取消 (spec 一.5): discard edits made since opening, restoring exactly
   * what the template had when this editor was opened. Does NOT close. */
  const revertToInitial = async () => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    const initial = savedInitialRef.current
    setFields(initial)
    try {
      // Cascade here too: reverting cutout back to what the template had on
      // open should also put background/shadow back in sync, not leave them
      // stuck showing "已失效，等待重新生成" for a cutout change that no
      // longer exists.
      const data = await apply(initial, true)
      onSaved(data)
      return data
    } catch {
      /* best-effort rollback; leave whatever last succeeded */
    }
  }

  const handleCancel = async () => {
    await revertToInitial()
    message.success(t('templateCutoutEditor.reverted'))
  }

  /** 恢复默认 (spec 一.5): reset to this editor's system defaults — only
   * changes the current in-editor/preview state; the user still needs to
   * click "保存" for the header to stop showing "未保存修改". */
  const handleResetToSystemDefaults = async () => {
    setFields(SYSTEM_DEFAULT_CUTOUT_FIELDS)
    try {
      await apply(SYSTEM_DEFAULT_CUTOUT_FIELDS)
    } catch {
      /* error already shown inline */
    }
  }

  /** 关闭 (spec 一.5): "没有未保存修改时直接关闭" — only when this editor's
   * eagerly-persisted live preview actually drifted from what the template
   * had on open (`isDirty`) is there anything to roll back server-side;
   * skipping `revertToInitial()` in the clean case avoids a pointless (and,
   * if another editor is saved around the same time, racy) full 抠图→色底→
   * 阴影 re-run on every plain close. */
  const requestClose = () => confirmDiscardAndClose(isDirty, async () => {
    if (isDirty) await revertToInitial()
    onClose()
  }, t('editorHeader.discardConfirm'))

  const previewUrl = bustUrl(current.preview_transparent_url, current.template_version)

  return (
    <Modal
      open={open}
      onCancel={requestClose}
      footer={null}
      closable={false}
      width={880}
      styles={{ body: { padding: 0 }, mask: { backgroundColor: 'transparent' } }}
      wrapClassName={MODAL_SURFACE_CLASS}
      destroyOnClose
    >
      <div className="tplcut-shell" data-testid="template-cutout-editor-modal">
        <EditorHeader
          testIdPrefix="tplcut"
          title={`${t('templateCutoutEditor.titlePrefix')} · ${current.name}`}
          dirty={isDirty}
          saving={applying}
          onCancel={handleCancel}
          onReset={handleResetToSystemDefaults}
          onSave={doSave}
          onClose={requestClose}
        />

        <div className="tplcut-body">
          <div className={`tplcut-preview stageflow-checker`}>
            {previewUrl ? <img src={previewUrl} alt={t('templateCutoutEditor.previewAlt')} /> : <span className="tplcut-preview-empty">{t('templateCutoutEditor.noPreview')}</span>}
            {applying && (
              <div className="tplcut-preview-busy">
                <LoadingOutlined spin style={{ fontSize: 22 }} />
                <span>{t('templateCutoutEditor.regenerating')}</span>
              </div>
            )}
          </div>

          <div className="tplcut-form">
            <p className="tplcut-hint">
              {t('templateCutoutEditor.hint')}
            </p>
            {error && <div className="tplcut-error">{error}</div>}

            <label className="apt-field">
              <span>{t('templateCutoutEditor.engine')}</span>
              <Select
                size="small"
                style={{ width: '100%' }}
                value={fields.engine}
                options={engines.map((e) => ({
                  value: e.name,
                  label: `${e.description || e.name}${e.available ? '' : t('templateCutoutEditor.engineUnavailable')}`,
                  disabled: !e.available,
                }))}
                onChange={(v) => scheduleApply({ ...fields, engine: v })}
              />
            </label>

            <div className="tplcut-slider-field">
              <div className="tplcut-slider-head">
                <span>{t('templateCutoutEditor.edgeSmooth')}</span>
                <span>{fields.edge_smooth.toFixed(1)}</span>
              </div>
              <Slider min={0} max={2} step={0.1} value={fields.edge_smooth} onChange={(v) => scheduleApply({ ...fields, edge_smooth: v })} />
            </div>

            <div className="tplcut-slider-field">
              <div className="tplcut-slider-head">
                <span>{t('templateCutoutEditor.feather')}</span>
                <span>{fields.feather.toFixed(1)}</span>
              </div>
              <Slider min={0} max={4} step={0.1} value={fields.feather} onChange={(v) => scheduleApply({ ...fields, feather: v })} />
            </div>

            <div className="tplcut-slider-field">
              <div className="tplcut-slider-head">
                <span>{t('templateCutoutEditor.erode')}</span>
                <span>{fields.erode}px</span>
              </div>
              <Slider min={0} max={10} value={fields.erode} onChange={(v) => scheduleApply({ ...fields, erode: v })} />
            </div>

            <div className="tplcut-slider-field">
              <div className="tplcut-slider-head">
                <span>{t('templateCutoutEditor.dilate')}</span>
                <span>{fields.dilate}px</span>
              </div>
              <Slider min={0} max={10} value={fields.dilate} onChange={(v) => scheduleApply({ ...fields, dilate: v })} />
            </div>

            <div className="tplcut-switch-row">
              <span>{t('templateCutoutEditor.decontaminate')}</span>
              <Switch size="small" checked={fields.decontaminate} onChange={(v) => scheduleApply({ ...fields, decontaminate: v })} />
            </div>

            {fields.decontaminate && (
              <div className="tplcut-slider-field">
                <div className="tplcut-slider-head">
                  <span>{t('templateCutoutEditor.decontaminateStrength')}</span>
                  <span>{Math.round(fields.decontaminate_strength * 100)}%</span>
                </div>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={fields.decontaminate_strength}
                  onChange={(v) => scheduleApply({ ...fields, decontaminate_strength: v })}
                />
              </div>
            )}
          </div>
        </div>

        <div className="tplcut-secondary-actions">
          <Button size="small" icon={<ReloadOutlined />} onClick={handleApplyNow} loading={applying}>
            {t('templateCutoutEditor.applyPreviewNow')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
